import {
  intranetPollResponseSchema,
  type UpsertIntranetPollRequest,
  type VoteIntranetPollRequest,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { intranetContentKey } from '@/products/intranet/features/intranet/use-intranet';

function pollKey(slug: string) {
  return [...intranetContentKey(slug), 'poll'] as const;
}

export function useIntranetPoll(slug: string | undefined) {
  return useQuery({
    queryKey: pollKey(slug ?? ''),
    queryFn: () =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug ?? '')}/poll`,
        intranetPollResponseSchema,
      ),
    enabled: Boolean(slug),
  });
}

export function upsertIntranetPoll(slug: string, body: UpsertIntranetPollRequest) {
  return apiFetch(
    `/intranet/contents/${encodeURIComponent(slug)}/poll`,
    intranetPollResponseSchema,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  );
}

export function useUpsertIntranetPoll(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertIntranetPollRequest) => upsertIntranetPoll(slug, body),
    onSuccess: (saved) => queryClient.setQueryData(pollKey(slug), saved),
  });
}

export function useDeleteIntranetPoll(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/intranet/contents/${encodeURIComponent(slug)}/poll`, intranetPollResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: (saved) => queryClient.setQueryData(pollKey(slug), saved),
  });
}

export function useVoteIntranetPoll(slug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VoteIntranetPollRequest) =>
      apiFetch(
        `/intranet/contents/${encodeURIComponent(slug)}/poll/votes`,
        intranetPollResponseSchema,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: (saved) => queryClient.setQueryData(pollKey(slug), saved),
  });
}
